import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Button,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Paper,
  Alert,
  Autocomplete,
  Tabs,
  Tab,
} from '@mui/material';
import AutoRenewIcon from '@mui/icons-material/Autorenew';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

import { useNavigate } from 'react-router-dom';
import { parseChessResultsTournament, recognizePlayer, quickRegisterPlayer, importChessResultsTournament } from '../../../services/parsing.service';
import { searchPlayers } from '../../../services/tournament.service';
import QuickRegisterDialog from './QuickRegisterDialog';

export default function ChessResultsImportDialog({ open, onClose }) {
  const navigate = useNavigate();
  const [urlOrId, setUrlOrId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]); // [{ orden, nombre, fideId, dbPlayer, status }]
  const [recognizing, setRecognizing] = useState(false);
  const [recognizingIndex, setRecognizingIndex] = useState(0);

  // States for Manual Association Dialog
  const [searchOpen, setSearchOpen] = useState(false);
  const [activePlayerIndex, setActivePlayerIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // States for Quick Registration
  const [quickRegOpen, setQuickRegOpen] = useState(false);
  const [quickRegLoading, setQuickRegLoading] = useState(false);

  const [activeTab, setActiveTab] = useState(0); // 0 = Jugadores, 1 = Partidas detectadas

  // Reset state when dialog closes
  const handleClose = () => {
    setUrlOrId('');
    setError(null);
    setTournament(null);
    setPlayers([]);
    setRecognizing(false);
    setRecognizingIndex(0);
    setActiveTab(0);
    onClose();
  };

  // Extract ID and parse tournament
  const handleLoadTournament = async () => {
    if (!urlOrId.trim()) return;
    setError(null);
    setLoading(true);
    setTournament(null);
    setPlayers([]);

    let id = urlOrId.trim();
    // Apply regexp to extract id from URL
    const match = id.match(/tnr(\d+)/);
    if (match) {
      id = match[1];
    }

    const tournamentId = parseInt(id, 10);
    if (isNaN(tournamentId)) {
      setError('Por favor, ingresa un ID o URL válido.');
      setLoading(false);
      return;
    }

    try {
      const data = await parseChessResultsTournament(tournamentId);
      setTournament(data);
      setActiveTab(0);
      if (data && data.jugadores) {
        const initialPlayers = data.jugadores.map((p) => ({
          orden: p.orden,
          nombre: p.nombre,
          fideId: p.fideId,
          dbPlayer: null,
          status: 'idle', // 'idle' | 'loading' | 'success' | 'failed'
        }));
        setPlayers(initialPlayers);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error al conectar con el servidor.');
    } finally {
      setLoading(false);
    }
  };

  // Run recognize players sequentially in real-time
  const handleRecognizePlayers = async () => {
    if (players.length === 0 || recognizing) return;
    setRecognizing(true);

    // Loop through players
    for (let i = 0; i < players.length; i++) {
      setRecognizingIndex(i + 1);
      // Set status to loading
      setPlayers((prev) => {
        const updated = [...prev];
        updated[i].status = 'loading';
        return updated;
      });

      try {
        const matchedPlayer = await recognizePlayer(players[i].nombre);
        setPlayers((prev) => {
          const updated = [...prev];
          if (matchedPlayer) {
            updated[i].dbPlayer = {
              jug_id: matchedPlayer.jug_id,
              apellido_nombre: `${matchedPlayer.APELLIDO}, ${matchedPlayer.NOMBRE}`,
              elo: matchedPlayer.ELO,
            };
            updated[i].status = 'success';
          } else {
            updated[i].dbPlayer = null;
            updated[i].status = 'failed';
          }
          return updated;
        });
      } catch (err) {
        console.error('Error recognizing player:', players[i].nombre, err);
        setPlayers((prev) => {
          const updated = [...prev];
          updated[i].status = 'failed';
          return updated;
        });
      }
      // Introduce a slight delay for aesthetic flow
      await new Promise((r) => setTimeout(r, 100));
    }

    setRecognizing(false);
  };

  // Open player search for manual selection
  const handleOpenSearch = (index) => {
    setActivePlayerIndex(index);
    setSearchQuery('');
    setSearchResults([]);
    setSearchOpen(true);
  };

  // Handle Search Input Debounce
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    const delayDebounce = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const results = await searchPlayers(searchQuery);
        setSearchResults(results || []);
      } catch (err) {
        console.error(err);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  // Associate player manually
  const handleSelectDbPlayer = (player) => {
    if (activePlayerIndex === null) return;
    setPlayers((prev) => {
      const updated = [...prev];
      updated[activePlayerIndex].dbPlayer = {
        jug_id: player.jug_id,
        apellido_nombre: player.apellido_nombre,
        elo: player.elo,
      };
      updated[activePlayerIndex].status = 'success';
      return updated;
    });
    setSearchOpen(false);
    setActivePlayerIndex(null);
  };

  // Submit quick register player
  const handleQuickRegisterSubmit = async (newPlayerData, resetForm) => {
    setQuickRegLoading(true);
    try {
      const result = await quickRegisterPlayer(newPlayerData);
      if (result && result.jug_id) {
        // Associate the newly registered player
        handleSelectDbPlayer({
          jug_id: result.jug_id,
          apellido_nombre: result.apellido_nombre,
          elo: result.elo ?? 0,
        });
        setQuickRegOpen(false);
        resetForm();
      } else {
        throw new Error('No se pudo registrar el jugador.');
      }
    } catch (err) {
      console.error(err);
      alert(err.message || 'Error al registrar al jugador.');
    } finally {
      setQuickRegLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    // 1. Validar que todos los jugadores tengan un jugador asignado
    const unassociated = players.filter(p => !p.dbPlayer || !p.dbPlayer.jug_id);
    if (unassociated.length > 0) {
      setError('Todos los jugadores deben tener un jugador asignado de la base de datos.');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      // Mapear los jugadores a { orden, dbJugId }
      const jugadoresMapeados = players.map(p => ({
        orden: p.orden,
        dbJugId: p.dbPlayer.jug_id,
      }));

      const importData = {
        chessResultsId: tournament.id,
        nombre: tournament.nombre,
        sistema: tournament.sistema || 1, // Por defecto Suizo (1) si no se especifica
        rondas: tournament.rondas,
        jugadoresMapeados,
      };

      const result = await importChessResultsTournament(importData);
      if (result && result.torId) {
        handleClose();
        navigate(`/tournaments/edit/${result.torId}`);
      } else {
        throw new Error('No se recibió el ID del torneo importado.');
      }
    } catch (err) {
      console.error('Error al confirmar importación:', err);
      setError(err.message || 'Error al importar el torneo.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogImportData = () => {
    if (!tournament) return;
    const jugadoresMapeados = players.map(p => ({
      orden: p.orden,
      dbJugId: p.dbPlayer?.jug_id || null,
    }));

    const importData = {
      chessResultsId: tournament.id,
      nombre: tournament.nombre,
      sistema: tournament.sistema || 1,
      rondas: tournament.rondas,
      jugadoresMapeados,
    };

    console.log('TEMPORAL IMPORT DATA:', importData);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 0,
            background: '#0f172a',
            backgroundImage: 'none',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: '#cbd5e1',
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', pb: 2, fontWeight: 600 }}>
          Importar desde Chess Results
        </DialogTitle>
        <DialogContent sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Top form */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
              <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                URL o ID del torneo
              </Typography>
              <TextField
                size="small"
                fullWidth
                placeholder="Ej: https://chess-results.com/tnr1342592.aspx o 1342592"
                value={urlOrId}
                onChange={(e) => setUrlOrId(e.target.value)}
                disabled={loading || recognizing}
                inputProps={{ style: { color: '#cbd5e1' } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0,
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                    '&:hover fieldset': { borderColor: '#3a6eb6' },
                    '&.Mui-focused fieldset': { borderColor: '#3a6eb6' },
                  },
                }}
              />
            </Box>
            <Button
              variant="contained"
              onClick={handleLoadTournament}
              disabled={loading || recognizing || !urlOrId.trim()}
              sx={{
                background: '#3a6eb6',
                borderRadius: 0,
                height: '40px',
                px: 4,
                '&:hover': { background: '#2a5793' },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Cargar'}
            </Button>
          </Box>

          {error && <Alert severity="error" sx={{ borderRadius: 0 }}>{error}</Alert>}

          {/* Tournament metadata & players */}
          {tournament && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  background: 'rgba(30, 41, 59, 0.5)',
                  borderColor: 'rgba(255, 255, 255, 0.08)',
                  borderRadius: 0,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>
                    Torneo Detectado
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#b1b2b3', fontWeight: 600 }}>
                    {tournament.nombre}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                    Origen: {tournament.fuente} | Rondas: {tournament.rondas?.length || 0} | Jugadores: {players.length}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<AutoRenewIcon />}
                  onClick={handleRecognizePlayers}
                  disabled={recognizing}
                  sx={{
                    background: '#1c5b77',
                    borderRadius: 0,
                    '&:hover': { background: '#356075ff' },
                  }}
                >
                  {recognizing ? `Reconociendo ${recognizingIndex}/${players.length}...` : 'Reconocer Jugadores'}
                </Button>
              </Paper>

              {/* Tabs selector */}
              <Box sx={{ borderBottom: 1, borderColor: 'rgba(255, 255, 255, 0.08)' }}>
                <Tabs
                  value={activeTab}
                  onChange={(e, val) => setActiveTab(val)}
                  textColor="inherit"
                  indicatorColor="primary"
                  sx={{
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#3a6eb6',
                    },
                    '& .MuiTab-root': {
                      color: '#94a3b8',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&.Mui-selected': {
                        color: '#cbd5e1',
                      },
                    }
                  }}
                >
                  <Tab label="Jugadores" />
                  <Tab label="Partidas detectadas" />
                </Tabs>
              </Box>

              {activeTab === 0 && (
                /* Table Container */
                <TableContainer sx={{ border: '1px solid rgba(255, 255, 255, 0.08)', maxHeight: '400px' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ background: '#1e293b', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)' }}>#</TableCell>
                        <TableCell sx={{ background: '#1e293b', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)' }}>Nombre (Chess Results)</TableCell>
                        <TableCell sx={{ background: '#1e293b', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)' }}>FIDE ID</TableCell>
                        <TableCell sx={{ background: '#1e293b', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)' }}>Jugador (Base de Datos)</TableCell>
                        <TableCell sx={{ background: '#1e293b', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)' }}>ID</TableCell>
                        <TableCell sx={{ background: '#1e293b', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)' }}>ELO</TableCell>
                        <TableCell align="right" sx={{ background: '#1e293b', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)' }}>Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {players.map((p, idx) => (
                        <TableRow key={p.orden || idx} sx={{ '&:hover': { background: '#2a334d !important' } }}>
                          <TableCell sx={{ color: '#94a3b8', py: 1, borderColor: 'rgba(255, 255, 255, 0.05)' }}>{p.orden}</TableCell>
                          <TableCell sx={{ color: '#cbd5e1', py: 1, fontWeight: 500, borderColor: 'rgba(255, 255, 255, 0.05)' }}>{p.nombre}</TableCell>
                          <TableCell sx={{ color: '#64748b', py: 1, borderColor: 'rgba(255, 255, 255, 0.05)' }}>
                            {p.fideId ? (
                              <a
                                href={`https://ratings.fide.com/profile/${p.fideId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: 'inherit', textDecoration: 'underline' }}
                              >
                                {p.fideId}
                              </a>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 1, borderColor: 'rgba(255, 255, 255, 0.05)' }}>
                            {p.status === 'loading' ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={16} />
                                <Typography variant="body2" sx={{ color: '#94a3b8' }}>Reconociendo...</Typography>
                              </Box>
                            ) : p.status === 'success' && p.dbPlayer ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#22c55e' }}>
                                <CheckCircleIcon fontSize="small" />
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{p.dbPlayer.apellido_nombre}</Typography>
                              </Box>
                            ) : p.status === 'failed' ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#ef4444' }}>
                                <CancelIcon fontSize="small" />
                                <Typography variant="body2">No se pudo reconocer al jugador</Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic' }}>Sin verificar</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ color: '#94a3b8', py: 1, borderColor: 'rgba(255, 255, 255, 0.05)' }}>{p.dbPlayer?.jug_id || '—'}</TableCell>
                          <TableCell sx={{ color: '#94a3b8', py: 1, borderColor: 'rgba(255, 255, 255, 0.05)' }}>{p.dbPlayer?.elo ?? '—'}</TableCell>
                          <TableCell align="right" sx={{ py: 1, borderColor: 'rgba(255, 255, 255, 0.05)' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={() => handleOpenSearch(idx)}
                              disabled={recognizing}
                              sx={{
                                color: '#3a6eb6',
                                borderColor: 'rgba(58, 110, 182, 0.25)',
                                borderRadius: 0,
                                py: 0.25,
                                '&:hover': {
                                  borderColor: '#3a6eb6',
                                  background: 'rgba(58, 110, 182, 0.05)',
                                },
                              }}
                            >
                              Cambiar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {activeTab === 1 && (
                <Box
                  sx={{
                    p: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '250px',
                    border: '1px dashed rgba(255, 255, 255, 0.1)',
                    background: 'rgba(30, 41, 59, 0.2)',
                  }}
                >
                  <Typography variant="body1" sx={{ color: '#cbd5e1', mb: 1, fontWeight: 600 }}>
                    Visualización de Partidas
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center', maxWidth: '400px' }}>
                    Próximamente: Listado de partidas y emparejamientos leídos de Chess Results a modo de validación visual e informativa antes de confirmar la importación.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(255, 255, 255, 0.08)', gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={recognizing}
            sx={{
              color: '#94a3b8',
              borderColor: 'rgba(255, 255, 255, 0.15)',
              borderRadius: 0,
              '&:hover': {
                borderColor: '#cbd5e1',
                background: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            Cancelar
          </Button>
          {tournament && (
            <Button
              variant="outlined"
              onClick={handleLogImportData}
              sx={{
                color: '#e2ba3c',
                borderColor: 'rgba(226, 186, 60, 0.3)',
                borderRadius: 0,
                '&:hover': {
                  borderColor: '#e2ba3c',
                  background: 'rgba(226, 186, 60, 0.05)',
                },
              }}
            >
              Imprimir Datos (Temp)
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleConfirmImport}
            disabled={loading || recognizing || !tournament}
            sx={{
              background: '#3a6eb6',
              borderRadius: 0,
              px: 4,
              '&:hover': { background: '#2a5793' },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Confirmar Importación'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Search Dialog */}
      <Dialog
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 0,
            background: '#1e293b',
            backgroundImage: 'none',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            color: '#cbd5e1',
            width: '100%',
            maxWidth: '500px',
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', pb: 1.5, fontWeight: 600 }}>
          Asociar Jugador
        </DialogTitle>
        <DialogContent sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {activePlayerIndex !== null && (
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              Buscando reemplazo para: <strong>{players[activePlayerIndex]?.nombre}</strong>
            </Typography>
          )}

          <Autocomplete
            value={null}
            onChange={(e, value) => value && handleSelectDbPlayer(value)}
            inputValue={searchQuery}
            onInputChange={(e, value) => setSearchQuery(value)}
            options={searchResults}
            getOptionLabel={(option) => `${option.apellido_nombre} (DNI: ${option.documento})`}
            loading={searchLoading}
            noOptionsText={searchQuery.length < 2 ? 'Escribe al menos 2 letras...' : 'No se encontraron jugadores'}
            PaperComponent={({ children }) => (
              <Paper sx={{ borderRadius: 0, background: '#0f172a', color: '#cbd5e1', border: '1px solid rgba(255, 255, 255, 0.15)' }}>
                {children}
              </Paper>
            )}
            ListboxProps={{
              sx: {
                backgroundColor: '#0f172a',
                color: '#94a3b8',
                '& .MuiAutocomplete-option': {
                  borderRadius: 0,
                  '&[aria-selected="true"]': {
                    backgroundColor: 'rgba(58, 110, 182, 0.25) !important',
                    color: '#cbd5e1',
                  },
                  '&[aria-selected="true"].Mui-focused': {
                    backgroundColor: 'rgba(58, 110, 182, 0.3) !important',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'rgba(255, 255, 255, 0.08) !important',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  },
                },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                label="Buscar Jugador en BD"
                placeholder="Ej: Pérez, Juan"
                InputLabelProps={{ style: { color: '#94a3b8' } }}
                inputProps={{ ...params.inputProps, style: { color: '#cbd5e1' } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0,
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)', borderRadius: 0 },
                    '&:hover fieldset': { borderColor: '#3a6eb6' },
                    '&.Mui-focused fieldset': { borderColor: '#3a6eb6' },
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />

          <Box sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.08)', pt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              ¿El jugador no existe en la base de datos? Regístralo de forma rápida:
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PersonAddIcon />}
              onClick={() => setQuickRegOpen(true)}
              sx={{
                color: '#3a6eb6',
                borderColor: 'rgba(58, 110, 182, 0.3)',
                borderRadius: 0,
                '&:hover': {
                  borderColor: '#3a6eb6',
                  background: 'rgba(58, 110, 182, 0.05)',
                },
              }}
            >
              Registrar nuevo jugador
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
          <Button
            variant="outlined"
            onClick={() => setSearchOpen(false)}
            sx={{
              color: '#94a3b8',
              borderColor: 'rgba(255, 255, 255, 0.15)',
              borderRadius: 0,
              '&:hover': {
                borderColor: '#cbd5e1',
                background: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Registration Sub-Dialog */}
      <QuickRegisterDialog
        open={quickRegOpen}
        onClose={() => setQuickRegOpen(false)}
        onSubmit={handleQuickRegisterSubmit}
        loading={quickRegLoading}
      />
    </>
  );
}
